/**
 * Contact Detail Page
 * Shows full contact information with related widgets:
 * - Account (linked customer)
 * - Leads (last 5-10)
 * - Quotes (last 5-10)
 * - Orders (last 5-10)
 * - Activities (recent)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { crmAPI, quotesAPI, adminAPI } from '../../utils/api';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  User,
  Phone,
  Mail,
  Building2,
  FileText,
  Calendar,
  CheckCircle,
  ClipboardList,
  ShoppingCart,
  Activity,
  Plus,
  Loader2,
  Star,
  TrendingUp,
  MessageSquare
} from 'lucide-react';

// Import shared components from the Unified Detail Page System
import { 
  DetailHeader, 
  DetailPageLayout, 
  ContentPanel, 
  ActionBar,
  RelatedOrdersTable,
  Widget,
  WidgetItem,
  WidgetTaskItem,
  WidgetOrderItem,
  WidgetActivityItem
} from '../../components/detail-system';

// Activity types for Quick Add
const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'note', label: 'Note' }
];

export default function ContactDetailPage({ 
  contactId, 
  onBack, 
  onNavigateToCustomer,
  onNavigateToLead,
  onNavigateToOrder,
  onNavigateToQuote
}) {
  const navigate = useNavigate();
  
  // Main data
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Related data
  const [account, setAccount] = useState(null);
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activities, setActivities] = useState([]);
  
  // Widget loading states
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Modal states
  const [showActivityModal, setShowActivityModal] = useState(false);
  
  // Form data
  const [activityForm, setActivityForm] = useState({
    type: 'call',
    subject: '',
    body: '',
    occurred_at: new Date().toISOString().slice(0, 16)
  });
  
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch contact data with related info
  const fetchContact = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await crmAPI.getContactWithRelated(contactId);
      setContact(data.contact);
      setAccount(data.account);
      setLeads(data.leads || []);
      setQuotes(data.quotes || []);
      setOrders(data.orders || []);
      setActivities(data.activities || []);
    } catch (err) {
      console.error('Failed to fetch contact:', err);
      setError('Failed to load contact details');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  // Fetch additional activities if needed
  const fetchActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const data = await crmAPI.getActivities({ contact_id: contactId, limit: 10 });
      setActivities(data.activities || []);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  }, [contactId]);

  // Initial data load
  useEffect(() => {
    if (contactId) {
      fetchContact();
    }
  }, [contactId, fetchContact]);

  // Handle activity logging
  const handleLogActivity = async () => {
    if (!activityForm.type) {
      alert('Activity type is required');
      return;
    }
    
    try {
      setSaving(true);
      await crmAPI.createActivity({
        ...activityForm,
        contact_id: contactId,
        account_id: contact?.account_id || null,
        occurred_at: new Date(activityForm.occurred_at).toISOString()
      });
      setShowActivityModal(false);
      setActivityForm({ type: 'call', subject: '', body: '', occurred_at: new Date().toISOString().slice(0, 16) });
      fetchActivities();
    } catch (err) {
      console.error('Failed to log activity:', err);
      alert('Failed to log activity: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchContact();
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchContact,
    enabled: !loading && !!contactId,
    refreshing,
    throttleMs: 1500,
  });

  // Format contact name
  const getContactName = () => {
    const parts = [contact?.first_name, contact?.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Unnamed Contact';
  };

  // Quick Actions
  const quickActions = [
    {
      id: 'log-activity',
      label: 'Log Activity',
      icon: Activity,
      onClick: () => setShowActivityModal(true)
    },
    {
      id: 'create-lead',
      label: 'Create Lead',
      icon: TrendingUp,
      onClick: () => onNavigateToLead?.({ create: true, contact_id: contactId })
    },
    {
      id: 'create-quote',
      label: 'Create Quote',
      icon: FileText,
      onClick: () => {
        if (contact?.account_id) {
          onNavigateToQuote?.({ create: true, account_id: contact.account_id });
        } else {
          alert('Link this contact to a customer first to create a quote.');
        }
      }
    },
    {
      id: 'create-order',
      label: 'Create Order',
      icon: ShoppingCart,
      onClick: () => {
        if (contact?.account_id) {
          onNavigateToOrder?.({ create: true, account_id: contact.account_id });
        } else {
          alert('Link this contact to a customer first to create an order.');
        }
      }
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="contact-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="contact-detail-error">
        <p className="text-red-600 mb-4">{error || 'Contact not found'}</p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const statusBadges = [];
  if (contact.is_primary) {
    statusBadges.push({ label: 'Primary Contact', color: 'blue' });
  }
  if (!contact.account_id) {
    statusBadges.push({ label: 'Unassigned', color: 'yellow' });
  }

  // Contact Status Config
  const CONTACT_STATUS_CONFIG = {
    primary: { label: 'Primary', color: 'bg-blue-100 text-blue-700' },
    active: { label: 'Active', color: 'bg-green-100 text-green-700' },
    unassigned: { label: 'Unassigned', color: 'bg-yellow-100 text-yellow-700' }
  };

  // Get status key
  const contactStatus = contact.is_primary ? 'primary' : (contact.account_id ? 'active' : 'unassigned');

  // Header Actions
  const headerPrimaryActions = [
    {
      label: 'Log Activity',
      icon: Activity,
      onClick: () => setShowActivityModal(true),
      testId: 'log-activity-btn'
    }
  ];

  // Quick Actions for ActionBar
  const quickActionsForBar = quickActions.map(action => ({
    ...action,
    testId: `quick-action-${action.id}`
  }));

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Leads Widget */}
      <Widget
        title="Leads"
        icon={TrendingUp}
        count={leads.length}
        loading={loadingLeads}
        onAdd={() => onNavigateToLead?.({ create: true, contact_id: contactId })}
        addLabel="Add"
        onViewAll={leads.length > 0 ? () => navigate(`/admin?tab=leads&contact_id=${contactId}&contact_name=${encodeURIComponent(contact?.name || contact?.first_name + ' ' + contact?.last_name || contactId)}`) : undefined}
        emptyMessage="No leads yet"
        emptyAction={{
          label: 'Create Lead',
          icon: Plus,
          onClick: () => onNavigateToLead?.({ create: true, contact_id: contactId })
        }}
      >
        {leads.slice(0, 5).map((lead) => (
          <WidgetItem
            key={lead.id}
            title={lead.id}
            subtitle={`${lead.source?.replace('_', ' ')} • ${lead.category || 'No category'}`}
            badge={lead.stage?.replace('_', ' ')}
            badgeColor={lead.stage === 'won' ? 'green' : lead.stage === 'lost' ? 'red' : 'default'}
            onClick={() => onNavigateToLead?.(lead.id)}
          />
        ))}
      </Widget>

      {/* Quotes Widget */}
      <Widget
        title="Quotes"
        icon={FileText}
        count={quotes.length}
        loading={loadingQuotes}
        onViewAll={quotes.length > 0 ? () => navigate(`/admin?tab=orders-quote-mode&contact_id=${contactId}&contact_name=${encodeURIComponent(contact?.name || contact?.first_name + ' ' + contact?.last_name || contactId)}`) : undefined}
        emptyMessage="No quotes yet"
      >
        {quotes.slice(0, 5).map((quote) => (
          <WidgetItem
            key={quote.id}
            title={quote.quote_number || quote.id}
            subtitle={`$${(quote.totals?.grand_total || quote.total || 0).toLocaleString()}`}
            badge={quote.status}
            badgeColor={quote.status === 'approved' ? 'green' : quote.status === 'sent' ? 'blue' : 'default'}
            onClick={() => onNavigateToQuote?.(quote.id)}
          />
        ))}
      </Widget>

      {/* Recent Activity Widget */}
      <Widget
        title="Recent Activity"
        icon={Activity}
        count={activities.length}
        loading={loadingActivities}
        onAdd={() => setShowActivityModal(true)}
        addLabel="Log"
        emptyMessage="No activity logged"
        emptyAction={{
          label: 'Log Activity',
          icon: Plus,
          onClick: () => setShowActivityModal(true)
        }}
      >
        {activities.slice(0, 5).map((activity) => (
          <WidgetActivityItem
            key={activity.id}
            type={activity.type}
            subject={activity.subject}
            body={activity.body}
            occurredAt={activity.occurred_at}
            createdBy={activity.created_by_user_id}
          />
        ))}
      </Widget>
    </>
  );

  return (
    <>
    <DetailPageLayout
      headerProps={{
        entityName: getContactName(),
        subtitle: `${contact.id} • ${contact.title || 'No title'}`,
        entityIcon: User,
        status: contactStatus,
        statusConfig: CONTACT_STATUS_CONFIG,
        onBack,
        backLabel: 'Back',
        onRefresh: handleRefresh,
        refreshing: refreshing,
        primaryActions: headerPrimaryActions,
        testId: 'contact-detail-header'
      }}
      sidebarContent={sidebarContent}
      testId="contact-detail-page"
    >
      {/* Quick Actions */}
      <ActionBar actions={quickActionsForBar} />

      {/* Contact Info Card */}
      <ContentPanel title="Contact Information" icon={User} testId="contact-info-panel">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Full Name</p>
            <p className="text-sm font-medium text-gray-900">{getContactName()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Title / Role</p>
            <p className="text-sm font-medium text-gray-900">{contact.title || 'Not set'}</p>
          </div>
          
          {contact.email && (
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 hover:underline">
                {contact.email}
              </a>
            </div>
          )}
          
          {contact.phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <a href={`tel:${contact.phone}`} className="text-sm text-blue-600 hover:underline">
                {contact.phone}
              </a>
            </div>
          )}
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Preferred Contact Method</p>
            <p className="text-sm font-medium text-gray-900">{contact.preferred_contact_method?.replace('_', ' ') || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Created</p>
            <p className="text-sm font-medium text-gray-900">{new Date(contact.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </ContentPanel>

      {/* Linked Account Card */}
      <ContentPanel title="Linked Customer" icon={Building2} testId="linked-customer-panel">
        {account ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{account.name}</p>
                <p className="text-sm text-gray-500 font-mono">{account.id}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onNavigateToCustomer?.(account.id)}
                data-testid="view-customer-btn"
              >
                View Customer
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <Building2 className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No customer linked</p>
            <p className="text-xs text-gray-400 mt-1">This contact is unassigned</p>
          </div>
        )}
      </ContentPanel>

      {/* Notes Card */}
      {contact.notes && (
        <ContentPanel title="Notes" icon={FileText} testId="notes-panel">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
        </ContentPanel>
      )}

      {/* Orders Widget - Using shared RelatedOrdersTable */}
      <RelatedOrdersTable
        orders={orders.map(o => ({
          ...o,
          order_id: o.order_id || o.id,
          created_at: o.created_at || o.updated_at,
          ops_status: o.status || o.ops_status,
          invoice_status: o.invoice_status || o.payment_status
        }))}
        total={orders.length}
        onViewOrder={(orderId) => onNavigateToOrder?.(orderId)}
        onViewAll={orders.length > 0 ? () => navigate(`/admin?tab=orders&contact_id=${contactId}&contact_name=${encodeURIComponent(contact?.name || contact?.first_name + ' ' + contact?.last_name || contactId)}`) : undefined}
        showCreateButton={false}
        maxItems={10}
        emptyMessage="No orders yet"
        testIdPrefix="contact-orders"
      />
    </DetailPageLayout>

    {/* Log Activity Modal */}
    <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
      <DialogContent className="max-w-md" data-testid="log-activity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Log Activity for {getContactName()}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Activity Type *</Label>
            <Select
              value={activityForm.type}
              onValueChange={(v) => setActivityForm({ ...activityForm, type: v })}
            >
              <SelectTrigger data-testid="activity-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Subject</Label>
            <Input
              value={activityForm.subject}
              onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })}
              placeholder="Brief summary..."
              data-testid="activity-subject-input"
            />
          </div>
          
          <div>
            <Label>Details</Label>
            <Textarea
              value={activityForm.body}
              onChange={(e) => setActivityForm({ ...activityForm, body: e.target.value })}
              placeholder="Add notes..."
              rows={3}
              data-testid="activity-body-input"
            />
          </div>
          
          <div>
            <Label>When</Label>
            <Input
              type="datetime-local"
              value={activityForm.occurred_at}
              onChange={(e) => setActivityForm({ ...activityForm, occurred_at: e.target.value })}
              data-testid="activity-datetime-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
          <Button 
            onClick={handleLogActivity} 
            disabled={saving}
            data-testid="save-activity-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Log Activity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
